import { ref, provide, inject } from 'vue'
import { createEndpoints } from '@/api/endpoints'

const USERS_KEY = Symbol('usersStore')

export function createUsersProvider(api) {
  const endpoints = createEndpoints(api)

  const users = ref([])
  const roles = ref([])
  const usersLoaded = ref(false)
  const rolesLoaded = ref(false)
  const loading = ref(false)
  const userRoles = new Map()
  const inFlightUserRoles = new Map()
  let inFlightUsers = null
  let inFlightRoles = null

  const ensureRoles = async ({ force = false } = {}) => {
    if (!force && rolesLoaded.value) {
      return roles.value
    }
    if (inFlightRoles) {
      return inFlightRoles
    }
    inFlightRoles = endpoints
      .getRoles()
      .then((data) => {
        roles.value = data.roles || []
        rolesLoaded.value = true
        return roles.value
      })
      .finally(() => {
        inFlightRoles = null
      })
    return inFlightRoles
  }

  const ensureUsers = async ({ force = false } = {}) => {
    if (!force && usersLoaded.value) {
      return users.value
    }
    if (inFlightUsers) {
      return inFlightUsers
    }
    inFlightUsers = endpoints
      .getUsers()
      .then((data) => {
        users.value = data.users || []
        usersLoaded.value = true
        return users.value
      })
      .finally(() => {
        inFlightUsers = null
      })
    return inFlightUsers
  }

  const ensureUsersPage = async ({ force = false } = {}) => {
    loading.value = true
    try {
      await Promise.all([
        ensureRoles({ force }),
        ensureUsers({ force }),
      ])
    } finally {
      loading.value = false
    }
  }

  const getUserRoles = async (userId, { force = false } = {}) => {
    const key = String(userId)
    if (!force && userRoles.has(key)) {
      return userRoles.get(key)
    }
    if (inFlightUserRoles.has(key)) {
      return inFlightUserRoles.get(key)
    }

    const promise = endpoints
      .getUserRoles(userId)
      .then((data) => {
        const list = data?.ok ? (data.roles || []) : []
        // Don't cache a failed lookup, or it looks like "no roles" forever.
        if (data?.ok) {
          userRoles.set(key, list)
        }
        return list
      })
      .finally(() => {
        inFlightUserRoles.delete(key)
      })

    inFlightUserRoles.set(key, promise)
    return promise
  }

  const invalidateUserRoles = (userId) => {
    userRoles.delete(String(userId))
  }

  const provider = {
    users,
    roles,
    loading,
    usersLoaded,
    rolesLoaded,
    ensureUsersPage,
    ensureUsers,
    ensureRoles,
    getUserRoles,
    invalidateUserRoles,
  }

  provide(USERS_KEY, provider)
  return provider
}

export function useUsers() {
  const store = inject(USERS_KEY)
  if (!store) {
    throw new Error('useUsers must be used within AppProvider')
  }
  return store
}
